import { ColumnsType } from 'antd/es/table';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { AppRoutes } from 'app/config/routerConfig/types';
import { useAppDispatch } from 'app/config/storeConfig/hooks';
import { Reservation, useGetPaginatedReservationsQuery } from 'entities/Reservation';
import { getUserName, useRole } from 'entities/User';
import { CancelReservationModal, cancelReservationModalActions } from 'features/CancelReservationModal';
import { TakeUpReservationModal, takeUpReservationModalActions } from 'widgets/TakeUpReservationModal';
import { FC, memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as CancelIcon } from 'shared/assets/icons/CancelIcon.svg';
import { ReactComponent as ContractsTableIcon } from 'shared/assets/icons/ContractsTableIcon.svg';
import { ReactComponent as ContractWhiteIcon } from 'shared/assets/icons/ContractWhiteIcon.svg';
import { ReactComponent as FiltersIcon } from 'shared/assets/icons/FiltersIcon.svg';
import { ReactComponent as PlusIcon } from 'shared/assets/icons/PlusIcon.svg';
import { ServerTable } from 'shared/ui/ServerTable';
import { BadgeColor, RowBadge } from 'shared/ui/ServerTable/RowBadge';
import { ServerTableActions, ServerTableRowActions } from 'shared/ui/ServerTable/types';
import { checkExpirationDate } from 'shared/utils/helpers/checkExpirationDate';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { CreateContractModal, createContractModalActions } from 'widgets/CreateContractModal';
import { ReservationDetailsDrawer, reservationDetailDrawerActions } from 'widgets/ReservationDetailsDrawer';
import { getSizeCodeTypeString } from 'entities/SizeCode';
import { formatDecimal } from 'shared/utils/helpers/formatDecimal';

export const ReservationsPage: FC = memo(() => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { isManagement, isPartner } = useRole();

  const { t } = useAppTranslation(['common', 'boxes']);

  const goToUserContracts = useCallback(
    (userId: string): void => {
      if (userId) {
        navigate(`${AppRoutes.USERS}/${userId}/contracts`);
      }
    },
    [navigate],
  );

  const openReservationDetails = useCallback(
    (reservationId: string | undefined): void => {
      if (reservationId) {
        dispatch(reservationDetailDrawerActions.setOpenReservationDetails(reservationId));
      }
    },
    [dispatch],
  );

  const openCancelReservationModal = useCallback(
    (reservation: Reservation): void => {
      dispatch(cancelReservationModalActions.openCancelReservationModal(reservation));
    },
    [dispatch],
  );

  const openTakeUpReservationModal = useCallback(
    (reservation: Reservation): void => {
      dispatch(takeUpReservationModalActions.openTakeUpReservationModal(reservation));
    },
    [dispatch],
  );

  const rowActions: Array<ServerTableRowActions<Reservation>> = useMemo(
    () => [
      {
        name: 'openBox',
        theme: 'primary',
        label: t('View details'),
        icon: <ContractWhiteIcon />,
        iconPosition: 'prev',
        onClick: (selectedReservation) => {
          openReservationDetails(selectedReservation.reservationId);
        },
      },
      // {
      //   name: 'edit',
      //   label: t('Edit'),
      //   icon: <EditIcon />,
      //   onClick: (selectedReservation) => {
      //     console.log('selectedReservation', selectedReservation);
      //   },
      // },
      {
        name: 'takeUp',
        theme: 'clear',
        label: t('Take up'),
        description: t('Take up'),
        icon: <ContractsTableIcon className="[&>path]:fill-primary" />,
        isDisabled: (reservation) =>
          checkExpirationDate(reservation.expirationDate) || Boolean(reservation.cancellationDate) || Boolean(reservation.takenUpDate),
        onClick: openTakeUpReservationModal,
      },
      {
        name: 'cancel',
        theme: 'clear',
        label: t('Cancel Reservation'),
        description: t('Cancel Reservation'),
        icon: <CancelIcon className="[&>path]:stroke-primary" />,
        isDisabled: (reservation) =>
          checkExpirationDate(reservation.expirationDate) || Boolean(reservation.cancellationDate) || Boolean(reservation.takenUpDate),
        onClick: openCancelReservationModal,
      },
    ],
    [openCancelReservationModal, openReservationDetails, openTakeUpReservationModal, t],
  );

  const openCreateReservationModal = useCallback((): void => {
    dispatch(createContractModalActions.setOpened(true));
  }, [dispatch]);

  const columns = useMemo<ColumnsType<Reservation>>(
    () => [
      {
        title: t('Customer'),
        key: 'userId',
        render: (_, record) => {
          const isExpiredReservation = checkExpirationDate(record.expirationDate);

          let badgeColor: BadgeColor = 'success';

          if (record.takenUpDate) badgeColor = 'accent';
          if (isExpiredReservation && !record.takenUpDate && !record.cancellationDate) badgeColor = 'error';
          if (record.cancellationDate && !record.takenUpDate) badgeColor = 'primaryLight';

          let badgeTitle = t('Active');

          if (record.takenUpDate) badgeTitle = t('Taken up');
          if (isExpiredReservation && !record.takenUpDate && !record.cancellationDate) badgeTitle = t('Expired');
          if (record.cancellationDate && !record.takenUpDate) badgeTitle = t('Cancelled');

          return (
            <div
              className="text-accent font-normal cursor-pointer underline py-6"
              onClick={() => {
                goToUserContracts(record.user.userId);
              }}
            >
              {getUserName(record.user)}
              <RowBadge color={badgeColor} title={badgeTitle} />
            </div>
          );
        },
      },
      {
        title: t('Warehouse'),
        key: 'warehouseId',
        render: (_, record) => (
          <div
            className="text-accent font-normal cursor-pointer underline"
            onClick={() => {
              // goToUserContracts(record.user.userId); // TODO GO TO WAREHOUSE PAGE
            }}
          >
            {record.warehouse.name}
          </div>
        ),
      },
      {
        title: t('Reservation number'),
        key: 'reservationNumber',
        render: (_, record) => (
          <div
            className="text-accent font-normal cursor-pointer underline"
            onClick={() => {
              openReservationDetails(record.reservationId);
            }}
          >
            #{record.reservationNumber}
          </div>
        ),
      },
      {
        title: t('Square'),
        key: 'square',
        render: (_, record) => (
          <div className="text-primary">
            <div>{record.boxes[0]?.name}</div>
            <div className="text-sm font-normal">
              {formatDecimal(record.boxes[0]?.square)} {getSizeCodeTypeString(record.boxes[0]?.sizeCode?.sizeCodeType)}
            </div>
          </div>
        ),
      },
      {
        title: t('Type'),
        key: 'invoiceFrequencyType',
        render: (_, record) => <div className="text-primary font-normal capitalize">{t(record.invoiceFrequencyType)}</div>,
      },
      {
        title: t('Expiry date'),
        key: 'expirationDate',
        render: (_, record) => {
          const isExpiredReservation = checkExpirationDate(record.expirationDate);

          return (
            <div className={isExpiredReservation ? 'text-error font-semibold' : 'text-primary font-normal'}>
              {getClientDateFormat(record.expirationDate)}
            </div>
          );
        },
      },
      {
        title: t('Date of creation'),
        key: 'createdAt',
        render: (_, record) => <div className="text-primary font-normal">{getClientDateFormat(record.createdAt)}</div>,
      },
      {
        title: t('Start date'),
        key: 'startRentDate',
        render: (_, record) => <div className="text-primary">{getClientDateFormat(record.startRentDate)}</div>,
      },
    ],
    [t, goToUserContracts, openReservationDetails],
  );

  const tableActions: Array<ServerTableActions<Reservation>> = useMemo(
    () => [
      {
        name: t('Create Reservation'),
        icon: <PlusIcon />,
        iconPosition: 'prev',
        onClick: openCreateReservationModal,
        disabled: isPartner,
      },
      {
        name: t('Filters'),
        icon: <FiltersIcon />,
        theme: 'secondary',
        iconPosition: 'prev',
        onClick: () => {
          console.log('onClick');
        },
      },
    ],
    [isPartner, openCreateReservationModal, t],
  );

  return (
    <div className="pt-4">
      <div className="font-semibold text-3xl mb-7 font-tektur">{t('Reservations')}</div>
      <ServerTable
        columns={columns}
        rowActions={isManagement ? rowActions : undefined}
        fetchData={useGetPaginatedReservationsQuery}
        defaultFilters={{
          enableExpired: true,
          enableCancelled: true,
          enableTakenUp: true,
          orderBy: 'createdAt:DESC',
        }}
        rowKey="reservationId"
        search
        tableActions={tableActions}
        tableActionsPosition="prev"
        searchPlaceholder={t('Enter reservation number')}
        rowActionsLimit={1}
      />
      <ReservationDetailsDrawer />
      <CancelReservationModal />
      <TakeUpReservationModal />
      <CreateContractModal />
    </div>
  );
});
